import { z } from 'zod';
import type { Feature, Polygon, MultiPolygon } from 'geojson';

export type ZipBoundary = Feature<Polygon | MultiPolygon, { zip: string }>;

export const idSchema = z.string().uuid();
export const zipSchema = z.string().regex(/^\d{5}$/, 'Enter a five-digit ZIP code.');
export const leadTypeSchema = z.enum(['buyer', 'seller', 'rental']);
export type LeadType = z.infer<typeof leadTypeSchema>;
export const pointSchema = z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) });
export type Point = z.infer<typeof pointSchema>;
export const MAX_RADIUS_MILES = 25;
export const areaRuleSchema = z.object({
  id: idSchema,
  name: z.string().trim().max(80).default(''),
  effect: z.enum(['preferred', 'backup', 'exclude']),
  shape: z.enum(['radius', 'zip_set']),
  enabled: z.boolean(),
  leadTypes: z.array(leadTypeSchema).min(1),
  centerZip: zipSchema.optional(),
  center: pointSchema.optional(),
  radiusMiles: z.number().nonnegative().max(MAX_RADIUS_MILES).optional(),
  zips: z.array(zipSchema).max(1000).default([]),
}).superRefine((rule, ctx) => {
  if (rule.shape === 'radius' && (!rule.centerZip || rule.radiusMiles === undefined || (rule.radiusMiles > 0 && !rule.center)))
    ctx.addIssue({ code: 'custom', message: 'Enter a ZIP and radius. A radius above zero also needs a ZIP location.' });
  if (rule.shape === 'zip_set' && !rule.zips.length)
    ctx.addIssue({ code: 'custom', message: 'Add at least one ZIP to this area.' });
}).transform(rule => {
  // Zero is exact ZIP membership, never a zero-size geographic circle.
  const normalized = rule.shape === 'radius' && rule.radiusMiles === 0
    ? { ...rule, shape: 'zip_set' as const, zips: [rule.centerZip!], centerZip: undefined, center: undefined, radiusMiles: undefined }
    : rule;
  return { ...normalized, name: serviceAreaLabel(normalized) };
});
export function serviceAreaLabel(rule: { shape: 'radius' | 'zip_set'; centerZip?: string; radiusMiles?: number; zips: string[] }) {
  if (rule.shape === 'zip_set') return rule.zips.length > 1 ? `${rule.zips.length} ZIP codes` : rule.zips[0] ? `ZIP ${rule.zips[0]}` : 'ZIP code';
  return rule.centerZip ? `ZIP ${rule.centerZip}${rule.radiusMiles ? ` · ${rule.radiusMiles} mi` : ''}` : 'ZIP code';
}
export type AreaRule = z.infer<typeof areaRuleSchema>;
export const coverageSchema = z.object({
  version: z.number().int().nonnegative(),
  backupEnabled: z.boolean(),
  dataset: z.string().min(1).max(100),
  rules: z.array(areaRuleSchema).min(1).max(40),
});
export type Coverage = z.infer<typeof coverageSchema>;
export const settingsSchema = z.object({
  weeklyCap: z.number().int().min(0).max(1000),
  enrolled: z.boolean(),
  minPrice: z.number().min(0),
  maxPrice: z.number().positive().nullable(),
  leadTypes: z.array(leadTypeSchema).min(1),
  languages: z.array(z.string().min(2).max(40)).min(1),
}).refine(x => x.maxPrice === null || x.minPrice <= x.maxPrice, 'Maximum price must be at least the minimum.');
export type AgentSettings = z.infer<typeof settingsSchema>;
export const policySchema = z.object({
  waterfallSlots: z.array(z.number().int().min(5).max(120)).min(1).max(20).default([20,15,15,10]),
  jumpBallSeconds: z.number().int().min(5).max(900).default(60),
  ringSeconds: z.number().int().min(10).max(60),
  holdSeconds: z.number().int().min(60).max(1800),
  searchSeconds: z.number().int().min(60).max(1800),
  allowBackup: z.boolean(),
  legacyFourHourBoost: z.boolean(),
  legacyPriceDirection: z.boolean(),
  weekTimezone: z.string().refine(x => { try { new Intl.DateTimeFormat('en', { timeZone: x }); return true; } catch { return false; } }),
  copyTrigger: z.literal('phone_acceptance'),
  // Accept historical policy JSON, but FUB delivery never gates phone bridging.
  blockBridgeUntilDelivered: z.boolean().default(false).transform(() => false as const),
}).superRefine((policy,ctx) => {
  if (policy.waterfallSlots.reduce((total,seconds)=>total+seconds,0)>policy.jumpBallSeconds)
    ctx.addIssue({code:'custom',path:['jumpBallSeconds'],message:'The jump-ball timer must allow time for all exclusive slots.'});
  if (policy.jumpBallSeconds>=policy.searchSeconds)
    ctx.addIssue({code:'custom',path:['searchSeconds'],message:'The search limit must extend beyond jump ball.'});
});
export type RoutingPolicy = z.infer<typeof policySchema>;
// Test policy, not an approval to enable production routing.
export const INITIAL_POLICY: RoutingPolicy = {
  waterfallSlots: [20,15,15,10], jumpBallSeconds: 60, ringSeconds: 30, holdSeconds: 600, searchSeconds: 300,
  allowBackup: true, legacyFourHourBoost: false, legacyPriceDirection: false,
  weekTimezone: 'America/New_York', copyTrigger: 'phone_acceptance', blockBridgeUntilDelivered: false,
};
export interface FubUser {
  id: number; name: string; email: string; phone: string | null;
  status: string; pauseLeadDistribution: boolean | null;
  teamIds: number[]; teamLeaderOf: number[];
}
export interface AccountView {
  id: string; label: string; externalId: string | null; accountName: string | null;
  enabled: boolean; health: string; setupState: string; agentCount: number;
  syncedAt: string | null; isSource: boolean; credentialOwner: number | null;
}
export interface AgentView {
  fubPhone?: string|null; phoneOverride?: string|null; phoneVersion?: number; smsResetAt?: string|null;
  sms?: {optedOut:boolean;replies:number;ready:boolean};
  id: string; accountId: string; fubUserId: number; name: string; email: string; phone: string | null;
  accountEnabled?: boolean; accountLabel: string; teamName: string; teams: { id: string; name: string; isDefault: boolean }[]; paused: boolean | null; fetchedAt: string | null;
  status: string; settings: AgentSettings; coverage: Coverage; settingsVersion: number;
  leadsThisWeek: number; lastPresentedAt: string | null; lastReceivedAt: string | null;
  performanceCount: number; available: boolean;
}
export interface LeadInput {
  handoff?: import('./handoff').ScriptHandoff;
  personId: number; firstName: string; lastName: string; zip: string; point: Point | null;
  price: number; leadType: LeadType; language: string;
  emails: Array<{ value: string; type?: string }>; phones: Array<{ value: string; type?: string }>;
}
export const routeInputSchema = z.object({
  programId: idSchema, operatorId: idSchema,
  personId: z.number().int().positive(),
  businessKey: z.string().min(8).max(160),
  zip: zipSchema, price: z.number().nonnegative(), leadType: leadTypeSchema,
  language: z.string().min(2).max(40).default('English'),
});
export type RouteInput = z.infer<typeof routeInputSchema>;
export interface CandidateView { id: string; agentId: string; name: string; accountLabel: string; rank: number; status: string; reason: string; tier: string; distance: number | null }
export interface RouteView {
  handoff?: import('./handoff').ScriptHandoff|null;
  waterfall?: {startedAt:string|null;jumpBallAt:string|null;jumpBall:boolean;offeredCount:number;waitingCount:number};
  id: string; requestId: string; operatorId: string; operatorName: string; leadName: string;
  zip: string; price: number; leadType: string; routeState: string;
  handoffState: string; conferenceState: string; conferenceName: string;
  bridgeNumber?: string | null; conferenceSid: string | null; destinationLabel: string | null; agentName: string | null;
  deliveryState: string | null; destinationPersonId: number | null;
  createdAt: string; holdUntil: string | null; releasedAt: string | null; version: number;
  activeOffer: { id: string; expiresAt: string; status: string; agentName: string } | null;
  candidates: CandidateView[];
}
export interface OfferPreview {
  handoff?: import('./handoff').HandoffBrief;
  handoffState?:string;
  id:string; name:string; zip:string; leadType:string; status:string; expiresAt:string;
  waterfall?:boolean; serverNow?:string; jumpBall?:boolean; jumpBallAt?:string|null;
  activeAgentName?:string|null; activeExpiresAt?:string|null; queuePosition?:number; canClaim?:boolean;
}
export interface OperatorView { id: string; name: string; externalId: string; number: string; pendingSessionId: string | null }
export interface ManagementUser { id: string; name: string; role: 'super_user' | 'team_leader'; organizationId: string; teamNames: string[] }
export interface WorkspaceLeader { id: string; name: string; teamNames: string[] }
export interface WorkspaceView { leaderId: string; leaderName: string; adminName: string; readOnly: true; expiresAt: string }
export interface AuditView { id: string; action: string; entityType: string; entityId: string; actor: string; createdAt: string; detail: string }
export interface Bootstrap {
  mode: 'demo' | 'live'; user: ManagementUser;
  workspace?: { canSwitch: boolean; view: WorkspaceView | null };
  program: { id: string; name: string; sourceLabel: string; policy: RoutingPolicy; policyVersion: number; enabled: boolean };
  accounts: AccountView[]; agents: AgentView[]; operators: OperatorView[]; routes: RouteView[]; audit: AuditView[];
}
export interface PostalPoint extends Point { zip: string; city: string; dataset: string }
